import React, { useState } from 'react';
import { Button, Dropdown, Modal } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { PaymentContext, paymentActionBlock } from './policy';
import { PAYMENT_METHOD_MESSAGE_DRAFT } from './messageDraft';

type Props = PaymentContext & {
    hideCharge?: boolean;
    isOps?: boolean;
    onCharge: () => void;
    onInvoice: () => void;
    /** Opens the existing composer with this draft in it. Never sends a patient message automatically. */
    onMessage?: (draft: string) => void;
    className?: string;
};

const explanations = {
    card: {
        title: 'No card on file',
        text: 'This patient does not have a card on file. Would you like to message them to add a payment method in their patient portal?',
    },
    'unknown-card': {
        title: 'Card status unavailable',
        text: 'We could not confirm whether this patient has a card on file. Check their payment method before charging, or open Messages to follow up.',
    },
    provider: {
        title: 'Choose a provider to continue',
        text: 'Open this patient in the appropriate provider view to charge or prepare an invoice. Payments must use that provider’s patient record and payment account.',
    },
};

export default function PaymentActions(props: Props) {
    const [pendingAction, setPendingAction] = useState<'charge' | 'invoice'>('charge');
    const [block, setBlock] = useState<ReturnType<typeof paymentActionBlock>>(null);
    const openAction = (action: 'charge' | 'invoice') => {
        const reason = paymentActionBlock(props);
        if (reason) { setPendingAction(action); setBlock(reason); return; }
        if (action === 'charge') props.onCharge();
        else props.onInvoice();
    };
    if (props.hideCharge || props.isOps) return null;
    const needsCard = block === 'card' || block === 'unknown-card';
    const explanation = block ? explanations[block] : undefined;
    return (
        <>
            <Dropdown trigger={['click']} className={props.className} menu={{ items: [
                { key: 'charge', label: 'Charge now', onClick: () => openAction('charge') },
                { key: 'invoice', label: 'Send invoice', onClick: () => openAction('invoice') },
            ] }}>
                <Button>Charge client <DownOutlined /></Button>
            </Dropdown>
            <Modal open={!!block} title={explanation?.title} onCancel={() => setBlock(null)} footer={[
                <Button key="cancel" onClick={() => setBlock(null)}>Cancel</Button>,
                ...(needsCard && pendingAction === 'invoice' && paymentActionBlock({ ...props, creditCardOnFile: true }) === null ? [
                    <Button key="invoice" onClick={() => { setBlock(null); props.onInvoice(); }}>Continue to invoice</Button>,
                ] : []),
                ...(needsCard && props.onMessage ? [
                    <Button key="message" type="primary" onClick={() => {
                        setBlock(null);
                        props.onMessage?.(PAYMENT_METHOD_MESSAGE_DRAFT);
                    }}>Message patient</Button>,
                ] : []),
            ]}>
                <p>{explanation?.text}</p>
                {needsCard && !props.onMessage && <p>Open Messages to ask the patient to add a payment method.</p>}
            </Modal>
        </>
    );
}
